import React from 'react';
import { connect } from 'react-redux';
import AdminShell, { Icon } from './AdminShell';
import { Fieldset, Field, FormActions, FormCard } from './Form';

/*
 * Site settings backed by real SiteSettings rows. Goals-client localisation
 * and emergency controls are intentionally omitted because this schema has no
 * rows or runtime consumers for them; displaying them would create fake state.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const EDITABLE = ['siteName', 'siteTitle', 'metaDescription', 'metaKeyword',
  'contactEmail', 'contactPhoneNumber', 'facebookLink', 'twitterLink',
  'instagramLink', 'youtubeLink', 'headerCode', 'bodyCode'];
const QUERY_FIELDS = EDITABLE.concat(['homeLogo', 'favicon', 'ogImage']);
const URL_FIELDS = ['facebookLink', 'twitterLink', 'instagramLink', 'youtubeLink'];

const Q = `query ($names: [String]) {
  getAdminSiteSettings(names: $names) {
    status errorMessage
    results { name value }
  }
}`;

const M_SAVE = `mutation (${EDITABLE.map(f => `$${f}: String`).join(', ')}) {
  updateSiteSettings(${EDITABLE.map(f => `${f}: $${f}`).join(', ')}) {
    status errorMessage
  }
}`;

const ok = res => res && Number(res.status) === 200;
const validUrl = value => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

class SiteSettings extends React.Component {
  state = {
    values: {}, original: {}, errors: {}, loading: true,
    error: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  isArabic = () => String(this.props.currentLocale || '').startsWith('ar');

  tx = (en, ar) => this.isArabic() ? ar : en;

  errorText = message => {
    if (!this.isArabic()) return message || 'Could not save the settings.';
    const value = String(message || '').toLowerCase();
    if (value.includes('site name')) return 'لا يمكن أن يكون اسم الموقع فارغاً.';
    if (value.includes('support email')) return 'أدخل بريداً إلكترونياً صالحاً للدعم.';
    if (value.includes('phone')) return 'أدخل رقم هاتف دعم صالحاً.';
    if (value.includes('url')) return 'أدخل رابطاً كاملاً يبدأ بـ http أو https.';
    if (value.includes('length')) return 'تتجاوز إحدى القيم الطول المدعوم.';
    return 'تعذّر حفظ إعدادات الموقع.';
  };

  load = async () => {
    this.setState({ loading: true, error: null });
    const json = await gql(Q, { names: QUERY_FIELDS }).catch(() => null);
    const res = json && json.data && json.data.getAdminSiteSettings;
    if (!ok(res)) {
      this.setState({
        loading: false,
        error: this.isArabic()
          ? 'تعذّر تحميل إعدادات الموقع.'
          : (res && res.errorMessage) || 'Could not load the settings.'
      });
      return;
    }
    const values = {};
    (res.results || []).forEach(r => { values[r.name] = r.value === null ? '' : r.value; });
    this.setState({ values, original: { ...values }, errors: {}, loading: false });
  };

  set = (name, value) => this.setState(st => ({
    values: { ...st.values, [name]: value },
    errors: { ...st.errors, [name]: null },
    notice: null
  }));

  dirty() {
    const { values, original } = this.state;
    return EDITABLE.some(k => (values[k] || '') !== (original[k] || ''));
  }

  reset = () => this.setState(st => ({
    values: { ...st.original }, errors: {}, notice: null
  }));

  validate() {
    const { values } = this.state;
    const errors = {};
    const required = this.tx('This field is required.', 'هذا الحقل مطلوب.');
    if (!String(values.siteName || '').trim()) errors.siteName = required;
    if (!String(values.contactEmail || '').trim()) errors.contactEmail = required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(values.contactEmail).trim())) {
      errors.contactEmail = this.tx('Enter a valid email address.', 'أدخل عنوان بريد إلكتروني صالحاً.');
    }
    if (values.contactPhoneNumber
      && !/^\+?[0-9][0-9\s().-]{5,24}$/.test(String(values.contactPhoneNumber).trim())) {
      errors.contactPhoneNumber = this.tx('Enter a valid phone number.', 'أدخل رقم هاتف صالحاً.');
    }
    EDITABLE.forEach(name => {
      const max = name === 'metaDescription' || name === 'metaKeyword' ? 10000 : 250;
      if (String(values[name] || '').length > max) {
        errors[name] = this.tx(`Use no more than ${max} characters.`, `استخدم ${max} حرفاً كحد أقصى.`);
      }
    });
    URL_FIELDS.forEach(name => {
      const value = String(values[name] || '').trim();
      if (value && !validUrl(value)) {
        errors[name] = this.tx('Enter a complete http or https URL.', 'أدخل رابطاً كاملاً يبدأ بـ http أو https.');
      }
    });
    return errors;
  }

  save = async () => {
    if (this.state.saving) return;
    const errors = this.validate();
    if (Object.keys(errors).length) {
      this.setState({
        errors,
        notice: { tone: 'danger', text: this.tx('Check the highlighted fields.', 'تحقق من الحقول المحددة.') }
      });
      return;
    }

    const values = EDITABLE.reduce((result, name) => {
      result[name] = String(this.state.values[name] || '').trim();
      return result;
    }, {});
    this.setState({ saving: true, notice: null });
    const json = await gql(M_SAVE, values).catch(() => null);
    const res = json && json.data && json.data.updateSiteSettings;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ notice: { tone: 'danger', text: this.errorText(res && res.errorMessage) } });
      return;
    }
    this.setState(st => ({
      values: { ...st.values, ...values },
      original: { ...st.original, ...values },
      errors: {},
      notice: {
        tone: 'info',
        text: this.tx('Settings saved. The website and apps use them on their next request.',
          'تم حفظ الإعدادات. سيستخدمها الموقع والتطبيقان في الطلب التالي.')
      }
    }));
  };

  field = (name, label, props = {}) => {
    const error = this.state.errors[name];
    const maxLength = props.maxLength || (props.long ? 10000 : 250);
    const common = {
      id: `site-${name}`,
      className: 'inp',
      dir: 'auto',
      value: this.state.values[name] || '',
      placeholder: props.placeholder,
      maxLength,
      required: !!props.required,
      'aria-invalid': !!error,
      onChange: e => this.set(name, e.target.value)
    };
    return (
      <Field label={label} hint={props.hint} error={error} span={props.span}
        required={props.required} htmlFor={common.id}>
        {props.textarea
          ? <textarea {...common} rows={props.rows || 2} />
          : <input {...common} type={props.type || 'text'} />}
      </Field>
    );
  };

  render() {
    const { loading, error, saving, notice, values } = this.state;
    const imageCount = ['homeLogo', 'favicon', 'ogImage'].filter(k => values[k]).length;

    return (
      <AdminShell
        current="site"
        breadcrumb={[this.tx('System', 'النظام'), this.tx('Site settings', 'إعدادات الموقع')]}
        title={this.tx('Site settings', 'إعدادات الموقع')}
        lead={this.tx('Identity and contact details used by the website and both apps.',
          'بيانات الهوية والتواصل المستخدمة في الموقع والتطبيقين.')}
        actions={[
          <a className="btn ghost" href="/siteadmin/settings/site" key="c">
            {this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}
          </a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`} role={notice.tone === 'danger' ? 'alert' : 'status'}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{this.tx('Dismiss', 'إغلاق')}</button>
          </div>
        )}

        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {this.tx(
              'Default language, timezone, emergency and ambulance numbers and trip sharing are not stored by this system, so they cannot be configured here. Base currency is under Currencies; version gates are under Mobile apps.',
              'لا يخزّن هذا النظام اللغة الافتراضية أو المنطقة الزمنية أو أرقام الطوارئ والإسعاف أو مشاركة الرحلة، لذلك لا يمكن ضبطها هنا. توجد العملة الأساسية ضمن العملات، وإصدارات التطبيق ضمن تطبيقات الجوال.'
            )}
          </span>
        </div>

        {error && (
          <div className="notice danger" role="alert">
            <span className="ni">!</span><span className="nt">{error}</span>
            <button className="btn ghost sm" type="button" onClick={this.load}>
              {this.tx('Try again', 'إعادة المحاولة')}
            </button>
          </div>
        )}

        {loading
          ? <section className="card"><div className="card-b">{this.tx('Loading settings…', 'جارٍ تحميل الإعدادات…')}</div></section>
          : !error && (
            <FormCard actions={
              <FormActions
                note={this.tx('Changes apply on the next website or app request.', 'تُطبّق التغييرات مع الطلب التالي من الموقع أو التطبيق.')}
                dirty={this.dirty()} busy={saving} onSave={this.save} onCancel={this.reset}
                saveLabel={this.tx('Save settings', 'حفظ الإعدادات')}
                cancelLabel={this.tx('Cancel', 'إلغاء')}
                busyLabel={this.tx('Saving…', 'جارٍ الحفظ…')}
              />
            }>
              <Fieldset title={this.tx('Identity', 'الهوية')}>
                {this.field('siteName', this.tx('Site name', 'اسم الموقع'), { required: true })}
                {this.field('siteTitle', this.tx('Tagline', 'الشعار النصي'), {
                  hint: this.tx('Shown beside the name on the website.', 'يظهر بجانب الاسم على الموقع.')
                })}
                {this.field('contactEmail', this.tx('Support email', 'بريد الدعم الإلكتروني'), {
                  type: 'email', required: true,
                  hint: this.tx('Shown to customers in both apps.', 'يظهر للعملاء في التطبيقين.')
                })}
                {this.field('contactPhoneNumber', this.tx('Support phone', 'هاتف الدعم'), {
                  type: 'tel', hint: this.tx('Dialled from the help screen in both apps.', 'يتم الاتصال به من شاشة المساعدة في التطبيقين.')
                })}
              </Fieldset>

              <Fieldset title={this.tx('Search engine', 'محركات البحث')}
                desc={this.tx("Used for the website's meta tags and link previews.", 'تُستخدم لوسوم الموقع ومعاينات الروابط.')}>
                {this.field('metaDescription', this.tx('Meta description', 'وصف الصفحة'), {
                  span: true, textarea: true, rows: 3, long: true
                })}
                {this.field('metaKeyword', this.tx('Meta keywords', 'الكلمات المفتاحية'), {
                  span: true, long: true,
                  hint: this.tx('Comma separated. Search engines largely ignore these now.', 'افصل بينها بفواصل. تتجاهل معظم محركات البحث هذه الكلمات حالياً.')
                })}
              </Fieldset>

              <Fieldset title={this.tx('Social links', 'روابط التواصل الاجتماعي')}
                desc={this.tx('Shown in the website footer. Leave one blank to hide it.', 'تظهر في تذييل الموقع. اترك الرابط فارغاً لإخفائه.')}>
                {this.field('facebookLink', 'Facebook', { type: 'url', placeholder: 'https://facebook.com/…' })}
                {this.field('instagramLink', 'Instagram', { type: 'url', placeholder: 'https://instagram.com/…' })}
                {this.field('twitterLink', 'X / Twitter', { type: 'url', placeholder: 'https://x.com/…' })}
                {this.field('youtubeLink', 'YouTube', { type: 'url', placeholder: 'https://youtube.com/…' })}
              </Fieldset>

              <Fieldset title={this.tx('Tracking codes', 'أكواد التتبع')}
                desc={this.tx(
                  'Paste tracking or verification scripts (Google Analytics/GA4, Meta Pixel, Google Tag Manager, Search Console). Header code is injected inside <head>; body code right after the opening <body> tag. Public website only.',
                  'ألصق أكواد التتبع أو التحقق (Google Analytics/GA4، Meta Pixel، Google Tag Manager، Search Console). يُدرَج كود الترويسة داخل <head>، وكود الجسم مباشرة بعد وسم <body>. للموقع العام فقط.'
                )}>
                {this.field('headerCode', this.tx('Header code', 'كود الترويسة'), {
                  span: true, textarea: true, rows: 6, long: true,
                  hint: this.tx('e.g. GA4 gtag snippet, Search Console verification meta tag.',
                    'مثال: كود gtag الخاص بـ GA4، أو وسم تحقق Search Console.')
                })}
                {this.field('bodyCode', this.tx('Body code', 'كود الجسم'), {
                  span: true, textarea: true, rows: 6, long: true,
                  hint: this.tx('e.g. Meta Pixel snippet (including its <noscript> fallback).',
                    'مثال: كود Meta Pixel (متضمناً بديل <noscript>).')
                })}
              </Fieldset>

              <Fieldset title={this.tx('Images', 'الصور')} rows>
                <div className="setting-row">
                  <div className="txt">
                    <b>{this.tx('Logo, favicon and link preview image', 'الشعار وأيقونة الموقع وصورة معاينة الرابط')}</b>
                    <span>{this.tx(
                      `${imageCount} of 3 set. Image uploads remain on the classic screen because they use separate upload endpoints.`,
                      `تم تعيين ${imageCount} من 3. تبقى عمليات رفع الصور في الشاشة الكلاسيكية لأنها تستخدم نقاط رفع منفصلة.`
                    )}</span>
                  </div>
                  <a className="btn ghost sm" href="/siteadmin/settings/site">
                    <Icon name="image" />{this.tx('Manage images', 'إدارة الصور')}
                  </a>
                </div>
              </Fieldset>
            </FormCard>
          )}
      </AdminShell>
    );
  }
}

const mapState = state => ({ currentLocale: state && state.intl && state.intl.locale });

export default connect(mapState)(SiteSettings);
